import { EmployeeRecord } from '../../utils/employee-indexeddb';
import { parseDepartmentHierarchy, UNASSIGNED_HEADQUARTER } from '../../utils/department-utils';
import { ApiKeyUsageData, ApiKeySummary, ApiKeyComparisonSummary } from './types';
import { normalizeHeadquarterName } from './normalize';
import { getUsageRateClass, getTierRateClass, formatNumber, formatDateRange, generateHeadquarterComment, maskEmail } from './format-helpers';
import { generateHeadquarterStatsWithComparison } from './stats';

/**
 * Generate HTML report with period-over-period comparison
 */
export function generateHeadquarterHtmlReportWithComparison(
  employees: EmployeeRecord[],
  currentPeriodUsage: ApiKeyUsageData[],
  previousPeriodUsage: ApiKeyUsageData[],
  reportTitle: string = 'API利用状況報告',
  currentPeriodLabel: string = '過去30日間',
  previousPeriodLabel: string = '30-60日前',
  currentPeriodDates?: { start: Date; end: Date },
  previousPeriodDates?: { start: Date; end: Date },
  apiKeySummary?: ApiKeyComparisonSummary,
  exchangeRate?: number | null,
  previousPeriodEmployees?: EmployeeRecord[]
): string {
  const stats = generateHeadquarterStatsWithComparison(employees, currentPeriodUsage, previousPeriodUsage, exchangeRate, previousPeriodEmployees);

  // Cost formatting: convert USD to JPY if exchange rate is available
  const rate = exchangeRate ?? null;
  const formatCost = (usdAmount: number): string => {
    if (rate) {
      const jpy = Math.round(usdAmount * rate);
      return `¥${jpy.toLocaleString()}`;
    }
    return `$${usdAmount.toFixed(2)}`;
  };

  // Use date ranges if provided, otherwise use labels
  const currentLabel = currentPeriodDates
    ? formatDateRange(currentPeriodDates.start, currentPeriodDates.end)
    : currentPeriodLabel;
  const previousLabel = previousPeriodDates
    ? formatDateRange(previousPeriodDates.start, previousPeriodDates.end)
    : previousPeriodLabel;

  // Calculate summary statistics for current period
  const totalHeadquarters = stats.length;
  const totalRequests = stats.reduce((sum, s) => sum + s.totalRequests, 0);
  const totalUsers = stats.reduce((sum, s) => sum + s.totalUsers, 0);
  const totalActive = stats.reduce((sum, s) => sum + s.activeUsers, 0);
  const totalCost = stats.reduce((sum, s) => sum + s.totalCost, 0);
  const averageRequests = totalActive > 0 ? totalRequests / totalActive : 0;
  const averageUsageRate = totalUsers > 0 ? (totalActive / totalUsers) * 100 : 0;

  const defaultCurrentSummary: ApiKeySummary = {
    totalApiKeys: currentPeriodUsage.length,
    activeApiKeys: currentPeriodUsage.filter(key => (key.requests ?? 0) > 0).length
  };
  const currentSummary = apiKeySummary?.current ?? defaultCurrentSummary;
  const activeApiKeys = currentSummary.activeApiKeys;

  // Unused variables - kept for potential future use
  void currentLabel;
  void previousLabel;

  // Prepare API key details for each headquarter
  const headquarterApiKeyDetails = new Map<string, Array<{apiKeyName: string, requests: number, cost: number, headquarter: string, department: string}>>();
  const apiKeyMap = new Map<string, ApiKeyUsageData>();

  currentPeriodUsage.forEach(apiKey => {
    apiKeyMap.set(apiKey.id, apiKey);
    apiKey.aliases?.forEach(alias => {
      if (!apiKeyMap.has(alias)) {
        apiKeyMap.set(alias, apiKey);
      }
    });
  });

  employees.forEach(employee => {
    let headquarter = employee.headquarter;

    if (!headquarter) {
      const parsed = parseDepartmentHierarchy(employee.department);
      headquarter = parsed.headquarter;
    }

    if (!headquarter && employee.department) {
      headquarter = UNASSIGNED_HEADQUARTER;
    }

    if (!headquarter) {
      headquarter = '未設定';
    }

    headquarter = normalizeHeadquarterName(headquarter);

    if (!headquarterApiKeyDetails.has(headquarter)) {
      headquarterApiKeyDetails.set(headquarter, []);
    }

    // Get department info
    let department = employee.departmentUnit;
    if (!department) {
      const parsed = parseDepartmentHierarchy(employee.department);
      department = parsed.department;
    }
    if (!department) {
      department = '—';
    }

    if (employee.apiKeyId) {
      const usage = apiKeyMap.get(employee.apiKeyId);
      if (usage) {
        // Check if this API key already exists in the list (avoid duplicates)
        const existingIndex = headquarterApiKeyDetails.get(headquarter)!.findIndex(
          detail => detail.apiKeyName === usage.name
        );

        if (existingIndex === -1) {
          headquarterApiKeyDetails.get(headquarter)!.push({
            apiKeyName: usage.name,
            requests: usage.requests,
            cost: usage.cost,
            headquarter: headquarter,
            department: department
          });
        }
      } else {
        // Has API key ID but no usage data found
        headquarterApiKeyDetails.get(headquarter)!.push({
          apiKeyName: employee.email || employee.apiKeyId,
          requests: 0,
          cost: 0,
          headquarter: headquarter,
          department: department
        });
      }
    } else {
      // No API key at all — unused employee
      headquarterApiKeyDetails.get(headquarter)!.push({
        apiKeyName: employee.email || employee.name || '不明',
        requests: 0,
        cost: 0,
        headquarter: headquarter,
        department: department
      });
    }
  });

  // Sort API keys by cost (descending)
  headquarterApiKeyDetails.forEach((details) => {
    details.sort((a, b) => b.cost - a.cost);
  });

  // Helper to render a small diff badge (e.g. "+2.1pt" or "-3.5pt")
  const diffBadge = (diff: number | undefined): string => {
    if (diff === undefined || diff === 0) return '';
    const sign = diff > 0 ? '+' : '';
    const color = diff > 0 ? 'var(--success)' : 'var(--danger)';
    return `<br><span style="font-size: 10px; font-weight: 400; color: ${color};">${sign}${diff.toFixed(1)}pt</span>`;
  };

  const numDiffBadge = (diff: number | undefined): string => {
    if (diff === undefined || diff === 0) return '';
    const sign = diff > 0 ? '+' : '';
    const color = diff > 0 ? 'var(--success)' : 'var(--danger)';
    return `<br><span style="font-size: 10px; font-weight: 400; color: ${color};">${sign}${formatNumber(diff)}</span>`;
  };

  // Generate grid rows with details/summary (no JS required)
  const tableRows = stats.map((stat) => {
    const c = stat.changes;

    // Get API key details for this headquarter
    const apiKeyDetails = headquarterApiKeyDetails.get(stat.headquarter) || [];
    const detailRows = apiKeyDetails.map((detail, idx) => {
      const isUnused = detail.requests === 0 && detail.cost === 0;
      const rowStyle = isUnused ? ' style="opacity: 0.5;"' : '';
      const costDisplay = isUnused
        ? '<span style="color: var(--danger); font-size: 11px;">未使用</span>'
        : formatCost(detail.cost);
      return `
                            <tr${rowStyle}>
                                <td>${idx + 1}</td>
                                <td>${maskEmail(detail.apiKeyName)}</td>
                                <td class="dept-name">${detail.department || '—'}</td>
                                <td class="number">${costDisplay}</td>
                                <td class="number">${formatNumber(detail.requests)}</td>
                            </tr>`;
    }).join('');

    // Generate comment for this headquarter
    const comment = generateHeadquarterComment(stat);

    return `
                    <details class="hq-details">
                        <summary class="grid-row">
                            <span class="dept-name">${stat.headquarter}</span>
                            <span class="usage-rate ${getUsageRateClass(stat.usageRate)}">
                              ${stat.usageRate.toFixed(1)}%${diffBadge(c?.usageRateDiff)}
                            </span>
                            <span class="usage-rate ${getTierRateClass(stat.costTier500Rate)}">
                              ${stat.costTier500Rate.toFixed(1)}%${diffBadge(c?.costTier500RateDiff)}
                            </span>
                            <span class="usage-rate ${getTierRateClass(stat.costTier2000Rate)}">
                              ${stat.costTier2000Rate.toFixed(1)}%${diffBadge(c?.costTier2000RateDiff)}
                            </span>
                            <span class="usage-rate ${getTierRateClass(stat.costTier4000Rate)}">
                              ${stat.costTier4000Rate.toFixed(1)}%${diffBadge(c?.costTier4000RateDiff)}
                            </span>
                            <span class="usage-rate ${getTierRateClass(stat.costTier8000Rate)}">
                              ${stat.costTier8000Rate.toFixed(1)}%${diffBadge(c?.costTier8000RateDiff)}
                            </span>
                            <span class="number">
                              ${formatNumber(stat.activeUsers)}${numDiffBadge(c?.activeUsersDiff)}
                            </span>
                            <span class="number">${formatNumber(stat.inactiveUsers)}</span>
                            <span class="number">${formatNumber(stat.totalUsers)}</span>
                            <span class="number">
                              ${formatNumber(stat.totalRequests)}${numDiffBadge(c?.requestsDiff)}
                            </span>
                            <span class="number">
                              ${formatCost(stat.totalCost)}
                            </span>
                            <span class="number">
                              ${formatCost(stat.costPerUser)}
                            </span>
                        </summary>
                        <div class="detail-panel">
                            <div class="detail-content">
                                <h4>APIキー別利用状況 - ${stat.headquarter}</h4>
                                <p class="detail-comment">${comment}</p>
                                ${apiKeyDetails.length > 0 ? `
                                <table class="detail-table">
                                    <thead>
                                        <tr>
                                            <th style="width: 50px;">#</th>
                                            <th>APIキー名</th>
                                            <th style="width: 120px;">部</th>
                                            <th style="width: 150px; text-align: right;">利用料金</th>
                                            <th style="width: 150px; text-align: right;">リクエスト数</th>
                                        </tr>
                                    </thead>
                                    <tbody>
${detailRows}
                                    </tbody>
                                </table>
                                ` : '<p style="color: var(--text-muted); text-align: center; padding: 20px;">利用中のAPIキーはありません</p>'}
                            </div>
                        </div>
                    </details>`;
  }).join('\n');

  const currentDate = new Date();
  const dateStr = `${currentDate.getFullYear()}年${currentDate.getMonth() + 1}月${currentDate.getDate()}日`;

  // Count HQs with 80%+ usage rate
  const highUsageHQs = stats.filter(s => s.usageRate >= 80).length;

  return `<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${reportTitle} - 本部別集計（以前比較）</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Sans+JP:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
        :root {
            --bg-primary: #0a0a0b;
            --bg-secondary: #111113;
            --bg-tertiary: #18181b;
            --bg-hover: #222225;
            --text-primary: #fafafa;
            --text-secondary: #a1a1aa;
            --text-muted: #71717a;
            --accent: #a78bfa;
            --accent-dim: rgba(167, 139, 250, 0.1);
            --success: #4ade80;
            --danger: #f87171;
            --border: #27272a;
            --border-light: #1f1f22;
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Inter', 'Noto Sans JP', -apple-system, BlinkMacSystemFont, sans-serif;
            background: var(--bg-primary);
            padding: 32px;
            min-height: 100vh;
            color: var(--text-primary);
            line-height: 1.6;
            -webkit-font-smoothing: antialiased;
        }

        .container {
            max-width: 1800px;
            margin: 0 auto;
            background: var(--bg-secondary);
            border-radius: 20px;
            border: 1px solid var(--border);
            overflow: hidden;
        }

        .header {
            background: var(--bg-secondary);
            padding: 48px 40px;
            text-align: center;
            border-bottom: 1px solid var(--border);
        }

        .header h1 {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 8px;
            letter-spacing: -0.5px;
            color: var(--text-primary);
        }

        .header p {
            font-size: 14px;
            color: var(--text-muted);
        }

        .period-comparison {
            display: flex;
            justify-content: center;
            gap: 12px;
            margin-top: 20px;
            flex-wrap: wrap;
        }

        .period-badge {
            background: var(--bg-tertiary);
            padding: 10px 20px;
            border-radius: 100px;
            font-size: 13px;
            font-weight: 500;
            color: var(--text-secondary);
            border: 1px solid var(--border);
        }

        .summary {
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            gap: 1px;
            background: var(--border);
        }

        .summary-card {
            background: var(--bg-secondary);
            padding: 28px 24px;
            text-align: center;
        }

        .summary-card h3 {
            font-size: 11px;
            color: var(--text-muted);
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 500;
        }

        .summary-card p {
            font-size: 32px;
            font-weight: 600;
            color: var(--text-primary);
            letter-spacing: -1px;
        }

        .summary-card small {
            display: inline-block;
            margin-top: 10px;
            font-size: 12px;
            font-weight: 500;
            color: var(--text-muted);
        }

        .analysis-section {
            padding: 32px 40px;
            background: var(--bg-secondary);
            border-top: 1px solid var(--border);
        }

        .analysis-section h3 {
            font-size: 14px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 16px;
            padding-left: 12px;
            border-left: 2px solid var(--accent);
        }

        .analysis-content {
            background: var(--bg-tertiary);
            padding: 24px;
            border-radius: 12px;
            border: 1px solid var(--border);
        }

        .analysis-content p {
            font-size: 14px;
            line-height: 1.8;
            color: var(--text-secondary);
            margin-bottom: 12px;
        }

        .analysis-content p:last-child {
            margin-bottom: 0;
        }

        .analysis-content strong {
            color: var(--text-primary);
            font-weight: 600;
        }

        .table-container {
            padding: 32px;
            background: var(--bg-secondary);
        }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
        }

        thead {
            background: var(--bg-tertiary);
        }

        th {
            padding: 14px 20px;
            text-align: left;
            font-weight: 500;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--text-muted);
            border-bottom: 1px solid var(--border);
        }

        td {
            padding: 16px 20px;
            border-bottom: 1px solid var(--border-light);
            color: var(--text-primary);
        }

        .dept-name {
            font-weight: 500;
            color: var(--text-primary);
        }

        .usage-rate {
            font-weight: 600;
        }

        .rate-high {
            color: var(--success);
        }

        .rate-medium {
            color: #fbbf24;
        }

        .rate-low {
            color: var(--danger);
        }

        .number {
            text-align: right;
            font-family: 'Inter', monospace;
            font-weight: 500;
            color: var(--text-secondary);
            font-variant-numeric: tabular-nums;
        }

        /* CSS Grid layout for main data grid */
        .grid-header,
        summary.grid-row {
            display: grid;
            grid-template-columns: 2fr repeat(5, 1fr) repeat(3, 0.8fr) repeat(3, 1fr);
            align-items: center;
        }

        .grid-header {
            background: var(--bg-tertiary);
            border-bottom: 1px solid var(--border);
        }

        .grid-header > span {
            padding: 14px 20px;
            font-weight: 500;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--text-muted);
        }

        .hq-details {
            border-bottom: 1px solid var(--border-light);
        }

        .hq-details summary {
            list-style: none;
            cursor: pointer;
        }

        .hq-details summary::-webkit-details-marker {
            display: none;
        }

        .hq-details summary::marker {
            display: none;
            content: '';
        }

        summary.grid-row {
            background: var(--bg-secondary);
            transition: background 0.15s ease;
        }

        summary.grid-row:hover {
            background: var(--bg-hover);
        }

        summary.grid-row > span {
            padding: 16px 20px;
        }

        .hq-details[open] > summary.grid-row {
            background: var(--bg-hover);
        }

        /* Expand icon via CSS pseudo-element */
        summary.grid-row > span:first-child::before {
            content: '▶';
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            background: var(--bg-tertiary);
            border-radius: 6px;
            font-size: 9px;
            color: var(--text-muted);
            margin-right: 10px;
            transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
            vertical-align: middle;
        }

        .hq-details[open] > summary.grid-row > span:first-child::before {
            transform: rotate(90deg);
            background: var(--accent-dim);
            color: var(--accent);
        }

        summary.grid-row:hover > span:first-child::before {
            background: var(--accent-dim);
            color: var(--accent);
        }

        .detail-panel {
            background: var(--bg-primary);
            padding: 24px 32px;
            border-bottom: 1px solid var(--border);
        }

        .detail-content {
            background: var(--bg-secondary);
            padding: 24px;
            border-radius: 12px;
            border: 1px solid var(--border);
        }

        .detail-content h4 {
            color: var(--text-primary);
            margin-bottom: 20px;
            font-size: 14px;
            font-weight: 500;
            padding-left: 12px;
            border-left: 2px solid var(--accent);
        }

        .detail-comment {
            font-size: 13px;
            color: var(--text-secondary);
            margin-bottom: 16px;
            padding: 12px 16px;
            background: var(--bg-tertiary);
            border-radius: 8px;
            border-left: 3px solid var(--accent);
            line-height: 1.6;
        }

        .detail-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
        }

        .detail-table th {
            background: var(--bg-tertiary);
            padding: 12px 16px;
            text-align: left;
            font-weight: 500;
            font-size: 11px;
            color: var(--text-muted);
            border-bottom: 1px solid var(--border);
        }

        .detail-table td {
            padding: 12px 16px;
            border-bottom: 1px solid var(--border-light);
        }

        .detail-table tbody tr:last-child td {
            border-bottom: none;
        }

        .detail-table tbody tr:hover td {
            background: var(--bg-hover);
        }

        .footer {
            padding: 24px 40px;
            background: var(--bg-tertiary);
            text-align: center;
            font-size: 12px;
            color: var(--text-muted);
            border-top: 1px solid var(--border);
        }

        @media (max-width: 1200px) {
            .summary {
                grid-template-columns: repeat(3, 1fr);
            }
        }

        @media (max-width: 768px) {
            body {
                padding: 16px;
            }

            .header {
                padding: 32px 24px;
            }

            .header h1 {
                font-size: 22px;
            }

            .summary {
                grid-template-columns: repeat(2, 1fr);
            }

            .summary-card {
                padding: 20px 16px;
            }

            .summary-card p {
                font-size: 24px;
            }

            .table-container {
                padding: 16px;
            }

            th, td {
                padding: 12px 10px;
            }
        }

        @media print {
            body {
                background: var(--bg-primary);
                padding: 0;
            }

            .container {
                border: none;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${reportTitle}</h1>
            <p>（${dateStr}時点） - 本部別集計 ※EXEVN・BPは除外</p>
        </div>

        <div class="summary">
            <div class="summary-card">
                <h3>総本部数</h3>
                <p>${formatNumber(totalHeadquarters)}</p>
            </div>
            <div class="summary-card">
                <h3>総リクエスト数</h3>
                <p>${formatNumber(totalRequests)}</p>
            </div>
            <div class="summary-card">
                <h3>総APIキー数</h3>
                <p>${formatNumber(currentSummary.totalApiKeys)}</p>
                <small style="font-size: 11px; color: var(--text-muted);">AnthropicとOpenAI APIキーの合算</small>
            </div>
            <div class="summary-card">
                <h3>アクティブAPIキー数</h3>
                <p>${formatNumber(activeApiKeys)}</p>
                <small style="font-size: 11px; color: var(--text-muted);">EXEVN・BP含む</small>
            </div>
            <div class="summary-card">
                <h3>アクティブユーザー数</h3>
                <p>${formatNumber(totalActive)}</p>
            </div>
            <div class="summary-card">
                <h3>平均利用率</h3>
                <p>${averageUsageRate.toFixed(1)}%</p>
            </div>
            <div class="summary-card">
                <h3>平均リクエスト回数</h3>
                <p>${averageRequests.toFixed(1)}</p>
            </div>
            <div class="summary-card">
                <h3>合計コスト</h3>
                <p>${formatCost(totalCost)}</p>
            </div>
        </div>
        <div class="analysis-section">
            <h3>利用状況分析</h3>
            <div class="analysis-content">
                <p><strong>現状:</strong> 全${formatNumber(totalUsers)}名中、アクティブユーザーは${formatNumber(totalActive)}名（利用率${averageUsageRate.toFixed(1)}%）。本統計はOpenAI APIおよびAnthropic APIの合算値です。Gemini等その他のAPI利用状況を踏まえると、実際の活用度はさらに高い可能性があります。一部の本部で利用が広まっていないことが全社指標に影響しており、まだまだ裾野が狭い状況です。</p>
                <p><strong>課題:</strong> IT業界全体で生成AI活用は急速に標準化しています。活用が進んでいる本部とそうでない本部の差が大きく、全社指標を押し下げています。生成AIを活用しないことは現状維持ではなく、日々競争力を失っている状態です。</p>
                <p><strong>今後のアクション:</strong> 利用率が80%を下回っている本部は、早急に80%まで上昇させましょう。利用率80%超の本部（現在${highUsageHQs}本部）についても、未利用者については業務効率化への非協力的姿勢の有無を確認し、必要に応じて業務命令としての活用促進を検討してください。</p>
            </div>
        </div>

        <div class="table-container">
            <div class="grid-header">
                <span>本部名</span>
                <span>利用率</span>
                <span>¥500以上<br>利用率</span>
                <span>¥2,000以上<br>利用率</span>
                <span>¥4,000以上<br>利用率</span>
                <span>¥8,000以上<br>利用率</span>
                <span>利用者数</span>
                <span>未利用者数</span>
                <span>総人数</span>
                <span>合計リクエスト</span>
                <span>合計コスト</span>
                <span>1人当たり<br>コスト</span>
            </div>
            <div class="grid-body">
${tableRows}
            </div>
        </div>

        <div class="footer">
            <p>※ OpenAI API・Anthropic APIの集計です。他のAPIを使用している場合は現時点では利用状況に反映されません。</p>
            <p>※ API提供元の仕様変更が頻繁に発生しており正確な集計ではないです。概算程度と思っていただければ幸いです。</p>
            <p>Generated by Usage Dashboard - ${new Date().toLocaleString('ja-JP')}</p>
        </div>
    </div>


</body>
</html>`;
}
